import { useEffect, useRef } from "react";
import { Eye, X } from "lucide-react";
import { useLocation } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import type { SafeUser } from "@shared/schema";

interface AuthMePreviewPayload extends SafeUser {
  previewingProjectId?: string | null;
  previewingProjectTitle?: string | null;
  previewingProjectSlug?: string | null;
}

function clearDealRoomQueries() {
  queryClient.removeQueries({
    predicate: (q) => {
      const key = String(q.queryKey[0] ?? "");
      return key.startsWith("/api/deal-room");
    },
  });
}

function clearPreviewFromCache() {
  queryClient.setQueryData(["/api/auth/me"], (old: AuthMePreviewPayload | null | undefined) =>
    old ? { ...old, previewingProjectId: null, previewingProjectTitle: null, previewingProjectSlug: null } : old,
  );
}

export function PreviewBanner() {
  const [location, navigate] = useLocation();

  const { data: authMe } = useQuery<AuthMePreviewPayload | null>({
    queryKey: ["/api/auth/me"],
    staleTime: Infinity,
  });

  // Manual exit — clears preview cookie, navigates back to Marketing tab.
  const stopMutation = useMutation({
    mutationFn: () => apiRequest("POST", "/api/admin/preview/stop", {}),
    onSuccess: () => {
      const projectId = authMe?.previewingProjectId;
      // Optimistically clear preview state BEFORE navigating to an admin route
      // so the auto-stop effect below doesn't re-fire and make a duplicate
      // /preview/stop request (previewingProjectId becomes null, effect exits early).
      clearPreviewFromCache();
      clearDealRoomQueries();
      void queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
      if (projectId) {
        navigate(`/admin/projects/${projectId}/marketing`);
      } else {
        navigate("/admin");
      }
    },
  });

  // Silent auto-stop — just clears state without redirecting.
  // Fired when the admin navigates away from the previewed project's surfaces.
  const silentStop = useMutation({
    mutationFn: () => apiRequest("POST", "/api/admin/preview/stop", {}),
    onSuccess: () => {
      clearPreviewFromCache();
      clearDealRoomQueries();
      void queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
    },
  });

  const projectId = authMe?.previewingProjectId;
  const projectSlug = authMe?.previewingProjectSlug;
  const silentStopRef = useRef(silentStop.mutate);
  silentStopRef.current = silentStop.mutate;

  // Auto-exit preview when the admin navigates away from the specific
  // investor-facing surfaces of the previewed project:
  //   /projects/<slug>   — the investor project landing page
  //   /deal-room/<id>    — the project's deal room
  //
  // Any other route (another project, dashboard, admin pages, etc.) exits
  // preview automatically so the admin doesn't accidentally operate in
  // preview context while on unrelated pages.
  useEffect(() => {
    if (!projectId) return;
    const isOnPreviewedProject =
      (projectSlug != null && location.startsWith(`/projects/${projectSlug}`)) ||
      location.startsWith(`/deal-room/${projectId}`);
    if (!isOnPreviewedProject && !stopMutation.isPending && !silentStop.isPending) {
      silentStopRef.current();
    }
  }, [location, projectId, projectSlug]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!authMe?.previewingProjectId) return null;

  const title = authMe.previewingProjectTitle ?? "Project";

  return (
    <div
      className="fixed top-0 left-0 right-0 z-[9999] flex items-center justify-between px-4 py-2 bg-amber-500 text-white shadow-md"
      data-testid="preview-banner"
    >
      <div className="flex items-center gap-2 text-sm font-medium">
        <Eye className="w-4 h-4 shrink-0" />
        <span>
          Previewing <strong>{title}</strong> as an investor — read-only
        </span>
      </div>
      <button
        onClick={() => stopMutation.mutate()}
        disabled={stopMutation.isPending}
        className="flex items-center gap-1.5 text-sm font-semibold bg-white/20 hover:bg-white/30 rounded-md px-3 py-1 transition-colors"
        data-testid="button-exit-preview"
      >
        <X className="w-3.5 h-3.5" />
        {stopMutation.isPending ? "Exiting…" : "Exit Preview"}
      </button>
    </div>
  );
}
